import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Receipt } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PayPalTrust } from "@/components/PayPalTrust";

export default function InvoicePay() {
  const { invoiceId } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/public/invoices/${invoiceId}`).then((r) => setInv(r.data)).catch(() => setInv(false)).finally(() => setLoading(false));
  }, [invoiceId]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { kind: "invoice", ref_id: invoiceId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPaying(false);
    }
  };

  return (
    <div className="bg-white text-brand-ink min-h-screen">
      <PublicNav />
      <section className="pt-32 pb-24 px-6 lg:px-10 max-w-2xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-red" /></div>
        ) : !inv ? (
          <div className="text-center py-20" data-testid="invoice-not-found"><p className="font-serif text-2xl text-brand-ink/60">Invoice not found.</p></div>
        ) : (
          <div className="rounded-[2rem] border border-brand-line overflow-hidden" data-testid="invoice-detail">
            <div className="bg-brand-ink text-white px-8 py-6 flex items-center justify-between">
              <div className="flex items-center gap-3"><Receipt className="w-5 h-5 text-brand-red" /><span className="font-display text-xl tracking-widest">{inv.number}</span></div>
              <span className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full ${inv.status === "paid" ? "bg-green-600" : "bg-brand-red"}`} data-testid="invoice-status">{inv.status}</span>
            </div>
            <div className="p-8">
              <div className="text-sm text-brand-ink/50">Billed to</div>
              <div className="font-medium text-lg">{inv.client_name}</div>
              <div className="mt-8 space-y-3">
                {inv.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-brand-line pb-3" data-testid={`invoice-item-${i}`}>
                    <span>{it.description} <span className="text-brand-ink/40">× {it.quantity}</span></span>
                    <span>${(it.quantity * it.rate).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-2 text-sm">
                <Row label="Subtotal" value={`$${inv.subtotal.toFixed(2)}`} />
                <Row label="Tax" value={`$${inv.tax.toFixed(2)}`} />
                <div className="flex justify-between font-display text-2xl pt-3 border-t border-brand-line"><span>Total</span><span>${inv.total.toFixed(2)}</span></div>
              </div>
              {inv.status !== "paid" && (
                <button onClick={pay} disabled={paying} className="mt-8 w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors disabled:opacity-60" data-testid="invoice-pay-btn">
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Pay ${inv.total.toFixed(2)}
                </button>
              )}
              {inv.status !== "paid" && <PayPalTrust className="mt-4" />}
              {inv.status === "paid" && <div className="mt-8 text-center text-green-700 font-medium" data-testid="invoice-paid-msg">This invoice has been paid. Thank you!</div>}
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-brand-ink/60"><span>{label}</span><span>{value}</span></div>
);
